/**
 * Route-level Suspense wrapper for lazy-loaded pages.
 *
 * Renders nothing for the first {@link delayMs} milliseconds so chunks that
 * arrive in under ~250 ms (a warm cache hit or fast network) feel instant
 * with no flash. After that, fades in a centered, aurora-themed loading
 * indicator so users on slower networks see a calm "we're working on it"
 * affordance instead of a blank viewport.
 *
 * Use this for top-level lazy routes wired in App.tsx (BrewDB, ModelBuilder,
 * Admin, Viz, Learn, Develop, Profile, TaskView, etc). For lazy components
 * that already render inside a populated layout (e.g. a tab content panel),
 * prefer an inline {@link LoadingSpinner}.
 *
 * A page chunk that fails to load (a tab left open across a release, or the
 * network dropped) is normally handled by `@/utils/preloadErrorReload`, which
 * reloads once. When it declines (it reloaded moments ago), the import error
 * reaches {@link ChunkErrorBoundary}, which offers a reload instead of the
 * blank screen an uncaught render error leaves.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Suspense } from 'react';
import { useLocation } from 'react-router';
import ChunkErrorBoundary from './ChunkErrorBoundary';

interface RouteSuspenseProps {
  children: ReactNode;
  /**
   * Delay before the spinner appears. Below ~250 ms users perceive the
   * transition as instant; we hide the fallback until then to avoid a
   * jarring flash on cached / fast loads.
   */
  delayMs?: number;
}

function RouteFallback({ delayMs }: Readonly<{ delayMs: number }>) {
  const [show, setShow] = useState(delayMs === 0);

  useEffect(() => {
    if (delayMs === 0) return;
    const id = window.setTimeout(() => setShow(true), delayMs);
    return () => window.clearTimeout(id);
  }, [delayMs]);

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center pointer-events-none"
      style={{
        opacity: show ? 1 : 0,
        transition: 'opacity 220ms ease-out',
      }}
      aria-live="polite"
      aria-busy="true"
    >
      <style>{`
        @keyframes brew-route-pulse {
          0%, 100% { transform: scale(1); opacity: 0.55; }
          50%      { transform: scale(1.18); opacity: 1; }
        }
        @keyframes brew-route-ring {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div className="flex flex-col items-center gap-4">
        <div className="relative size-16">
          {/* Soft outer halo */}
          <div
            className="absolute inset-0 rounded-full blur-xl"
            style={{
              background:
                'radial-gradient(circle, rgb(var(--brew-blue-light) / 0.45) 0%, rgb(var(--brew-blue) / 0.15) 55%, transparent 75%)',
              animation: 'brew-route-pulse 1.8s ease-in-out infinite',
            }}
          />
          {/* Spinning ring */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              border: '2px solid rgb(var(--brew-blue-light) / 0.18)',
              borderTopColor: '#3cb4e5',
              animation: 'brew-route-ring 0.9s linear infinite',
            }}
          />
          {/* Center dot */}
          <div
            className="absolute left-1/2 top-1/2 size-2 rounded-full -translate-1/2"
            style={{
              background: '#3cb4e5',
              boxShadow: '0 0 12px rgb(var(--brew-blue-light) / 0.8)',
            }}
          />
        </div>
        <p className="text-sm tracking-wide text-aurora-text/70" >
          Loading…
        </p>
      </div>
    </div>
  );
}

export default function RouteSuspense({ children, delayMs = 200 }: Readonly<RouteSuspenseProps>) {
  const { pathname } = useLocation();
  return (
    <ChunkErrorBoundary resetKey={pathname}>
      <Suspense fallback={<RouteFallback delayMs={delayMs} />}>{children}</Suspense>
    </ChunkErrorBoundary>
  );
}
